import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Ticket } from '@app/shared/models/classes/Ticket';
import { LoadingBarService } from '@app/shared/services/local/loading-bar/loading-bar.service';
import { ToastrService } from '@app/shared/services/toast.service';
import { Observable, throwError } from 'rxjs';
import { catchError, mergeMap } from 'rxjs';

import { AuthenticationService } from './../authentication/authentication.service';
import { ForbiddenService } from '@app/shared/services/forbidden.service';
import { __ } from '@app/shared/functions/object.functions';

@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  private injector = inject(Injector);
  private router = inject(Router);
  private toastr = inject(ToastrService);
  private forbiddenService = inject(ForbiddenService);
  private loadingBarService = inject(LoadingBarService);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const authenticationService = this.injector.get(AuthenticationService);

    return next.handle(request.clone()).pipe(
      catchError((response: unknown) => {
        const httpResponse = response as HttpErrorResponse;
        if (httpResponse.status === 401 && request.method.toLowerCase() !== 'options') {
          // TODO: QUEUE MULTIPLE REQUEST AND JUST SEND OUT ONE TOKEN REQUEST
          return authenticationService.refreshToken().pipe(
            mergeMap((_accessToken: Ticket) => {
              const credentials = authenticationService.credentials;
              if (!credentials) {
                return throwError(() => new Error('Authentication refresh completed without credentials.'));
              }
              return next.handle(
                request.clone({
                  setHeaders: { Authorization: `Bearer ${credentials.access_token}` },
                }),
              );
            }),
            catchError((error: unknown) => {
              const httpError = error as HttpErrorResponse;
              switch (httpError.status) {
                case 400:
                case 401:
                case 403:
                  if (__.IsNullOrUndefined(httpError.error) || __.IsNullOrUndefinedOrEmpty(httpError.error.error)) {
                    break;
                  }
                  authenticationService.logout();
                  this.toastr.error('You are not authorized to view this site. Please login.');
                  this.router.navigate(['/login']).then((_data: boolean) => {
                    this.loadingBarService.complete();
                  });

                  return throwError(() => response);

                default:
                  break;
              }

              return throwError(() => error);
            }),
          );
        }

        if (httpResponse.status === 403) {
          this.forbiddenService.forbid();
          this.loadingBarService.complete();
        }

        return throwError(() => response);
      }),
    );
  }
}
